import React, { useState } from 'react';
import { Check, CheckCircle2, FileSignature, Plus, X, XCircle } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { SkeletonText } from '../../../shared/components/Skeleton';
import { Textarea } from '../../../shared/components/Textarea';
import { authService } from '../../../shared/lib/auth';
import {
  useAditivos,
  useAprovarAditivo,
  useConcluirAditivo,
  useRejeitarAditivo,
} from '../hooks';
import type { Addendum } from '../api';

interface Props {
  contractId: string;
  canCreate: boolean;
  canEdit: boolean;
  onCreateClick: () => void;
}

const SITUACAO_LABEL: Record<Addendum['situacao'], string> = {
  em_elaboracao: 'Em Elaboração',
  em_aprovacao: 'Em Aprovação',
  aprovado: 'Aprovado',
  rejeitado: 'Rejeitado',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

const SITUACAO_COLOR: Record<Addendum['situacao'], string> = {
  em_elaboracao: 'bg-gray-100 text-gray-700',
  em_aprovacao: 'bg-blue-100 text-blue-800',
  aprovado: 'bg-green-100 text-green-800',
  rejeitado: 'bg-red-100 text-red-700',
  concluido: 'bg-emerald-100 text-emerald-800',
  cancelado: 'bg-gray-100 text-gray-500',
};

/** Listagem de aditivos + aprovação/rejeição (RN-COM-006/007). */
export function ContractAddendaSection({ contractId, canCreate, canEdit, onCreateClick }: Props) {
  const { data: addenda = [], isLoading } = useAditivos(contractId);
  const { mutate: aprovar, isPending: isApproving } = useAprovarAditivo();
  const { mutate: rejeitar, isPending: isRejecting } = useRejeitarAditivo();
  const { mutate: concluir, isPending: isCompleting } = useConcluirAditivo();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const currentUserId = authService.getCurrentUser()?.id;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">Aditivos</h2>
        {canCreate && (
          <Button size="sm" icon={<Plus className="w-3.5 h-3.5" />} onClick={onCreateClick}>
            Criar Aditivo
          </Button>
        )}
      </div>

      {isLoading ? (
        <SkeletonText lines={3} />
      ) : addenda.length === 0 ? (
        <EmptyState size="compact" icon={<FileSignature className="h-8 w-8" />} title="Nenhum aditivo criado ainda" />
      ) : (
        <ul className="divide-y divide-gray-100 text-sm space-y-0">
          {addenda.map((a) => {
            const myApprover = a.approvers.find((ap) => ap.userId === currentUserId);
            const canDecide = a.situacao === 'em_aprovacao' && myApprover && myApprover.decision == null;
            const isRejectingThis = rejectingId === a.id;

            return (
              <li key={a.id} className="py-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-800">
                      {a.code} <span className="text-xs text-gray-400">v{a.versionNumber}</span>
                    </p>
                    <p className="text-xs text-gray-500">{a.justificativa}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${SITUACAO_COLOR[a.situacao]}`}>
                    {SITUACAO_LABEL[a.situacao]}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1">
                  {a.scopeChanges.map((sc, i) => (
                    <span key={i} className="text-xs bg-gray-50 text-gray-600 px-1.5 py-0.5 rounded border border-gray-200">
                      {sc.type}
                    </span>
                  ))}
                </div>

                {a.approvers.length > 0 && (
                  <ul className="text-xs text-gray-500 space-y-0.5">
                    {a.approvers.map((ap) => (
                      <li key={ap.id}>
                        {ap.decision === 'aprovado' && <Check className="inline w-3 h-3 text-green-600 mr-1" />}
                        {ap.decision === 'rejeitado' && <X className="inline w-3 h-3 text-red-600 mr-1" />}
                        Aprovador {ap.userId === currentUserId ? '(você)' : ap.userId}
                        {ap.decision ? ` — ${ap.decision}` : ' — pendente'}
                      </li>
                    ))}
                  </ul>
                )}

                {canDecide && !isRejectingThis && (
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" loading={isApproving} icon={<Check className="w-3.5 h-3.5" />} onClick={() => aprovar({ id: contractId, addendumId: a.id })}>
                      Aprovar
                    </Button>
                    <Button size="sm" variant="secondary" disabled={isRejecting} icon={<XCircle className="w-3.5 h-3.5" />} onClick={() => setRejectingId(a.id)}>
                      Rejeitar
                    </Button>
                  </div>
                )}

                {isRejectingThis && (
                  <div className="space-y-2 pt-1">
                    <Textarea
                      label="Justificativa da rejeição *"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                      showCharCount
                      minLength={10}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="danger"
                        loading={isRejecting}
                        disabled={rejectReason.trim().length < 10}
                        onClick={() => {
                          rejeitar(
                            { id: contractId, addendumId: a.id, justificativa: rejectReason },
                            { onSuccess: () => { setRejectingId(null); setRejectReason(''); } },
                          );
                        }}
                      >
                        Confirmar rejeição
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setRejectReason(''); }}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}

                {canEdit && a.situacao === 'aprovado' && (
                  <Button size="sm" loading={isCompleting} icon={<CheckCircle2 className="w-3.5 h-3.5" />} onClick={() => concluir({ id: contractId, addendumId: a.id })}>
                    Aplicar aditivo ao contrato
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
